import { requestData } from "../api-config";

import { AnsweredFormDetail } from "@/types/forms/answered-form";
import {
  FormDetail,
  FormSubmissionResponse,
  FormTinyWithStatus,
} from "@/types/forms/form";

export async function getForm(
  formPath: string,
  patientId: string | null = null,
  membershipId: string | null = null,
  controller: AbortController | null = null,
): Promise<AnsweredFormDetail | null> {
  return await requestData<AnsweredFormDetail>(
    // Remove trailing dot from formPath in case the texts include a period
    `forms/by-path/${formPath.replace(/\.$/, "")}`,
    "get",
    {
      searchParams: {
        patientId,
        membershipId,
      },
      signal: controller?.signal,
    },
    false,
  );
}

export async function formExists(formPath: string) {
  return await requestData<boolean>(
    // Remove trailing dot from formPath in case the texts include a period
    `forms/${formPath.replace(/\.$/, "")}/exists`,
    "get",
  );
}

// `membershipId` counts only submissions signed for that plan (or for none).
export async function getFormsWithStatus(
  formPaths: string[],
  patientId: string,
  membershipId?: string,
): Promise<FormTinyWithStatus[] | null> {
  const cleanedPaths = formPaths.map((path) => path.replace(/\.$/, ""));

  return await requestData<FormTinyWithStatus[]>(
    "forms/with-status",
    "get",
    {
      searchParams: {
        form_paths: cleanedPaths.join(","),
        patient_id: patientId,
        ...(membershipId ? { membership_id: membershipId } : {}),
      },
    },
    false,
  );
}

export async function formsExist(formPaths: string[]): Promise<boolean> {
  // Clean form paths by removing trailing dots
  const cleanedPaths = formPaths.map((path) => path.replace(/\.$/, ""));

  const result = await requestData<boolean>("forms/batch/exists", "get", {
    searchParams: {
      form_paths: cleanedPaths.join(","),
    },
  });

  return result ?? false;
}

export async function submitForm(
  formId: string,
  data: any,
  isCompleteSubmission: boolean = true,
): Promise<FormSubmissionResponse | null> {
  const submissionData = {
    ...data,
    is_complete_submission: isCompleteSubmission,
  };

  const response = await requestData<FormSubmissionResponse | boolean>(
    `forms/${formId}/submit`,
    "put",
    {
      json: submissionData,
      searchParams: { include_answered_form_id: true },
      timeout: 40000,
    },
    false,
  );

  return typeof response === "boolean" ? { success: response } : response;
}

export async function listForms(
  params: URLSearchParams,
): Promise<FormDetail[] | null> {
  return await requestData<FormDetail[]>(`forms/list`, "get", {
    searchParams: params,
  });
}

export async function uploadFormFile(
  file: File,
): Promise<{ path: string; signedUrl: string } | null> {
  const formData = new FormData();
  formData.append("file", file);
  return await requestData<{ path: string; signedUrl: string }>(
    "forms/file/upload",
    "post",
    {
      body: formData,
    },
    false,
    60000, // 60 seconds for file uploads
  );
}
